import { Text } from '@repo/foundations';
import type { ReactNode } from 'react';
import type { IIntegration } from '../../api';
import type { IntegrationSpec } from '../../interfaces/IntegrationSpec';
import { closeAllModals, openModal } from '../ModalManager';
import { IdentityTokenModal } from './IdentityTokenModal';

/**
 * Determines if OIDC is enabled in the integration spec.
 * @param integrationSpec - The integration spec to check.
 * @param selectedGroup - The selected group to check.
 * @returns True if OIDC is enabled, otherwise false.
 */
function isOidcEnabledInSpec(
	integrationSpec: IntegrationSpec,
	selectedGroup: string
): boolean {
	// Check if it's a builtin integration with OIDC group settings
	if (integrationSpec.type === 'builtin') {
		const groupSettings = integrationSpec.value.groupSettings;

		if (groupSettings?.[selectedGroup]?.isOidc === true) {
			return true;
		}
	}

	return false;
}

/**
 * Determines if the integration is using OIDC.
 * @param integration - The integration to check.
 * @returns True if OIDC is being used, otherwise false.
 */
function isIntegrationUsingOidc(integration: IIntegration): boolean {
	if (integration.type === 'microstrategy') {
		return integration.credentials?._group === 'OIDC';
	}
	return false;
}

function shouldUseOidc(
	integrationSpec?: IntegrationSpec,
	selectedGroup?: string,
	integration?: IIntegration
): boolean {
	/* NOTE: One of ( integration ) or ( selectedGroup & integrationSpec ) should be provided. */

	// For syncs, check the actual integration credentials
	if (integration) {
		return isIntegrationUsingOidc(integration);
	}

	// For connection setup, check the spec
	if (selectedGroup && integrationSpec) {
		return isOidcEnabledInSpec(integrationSpec, selectedGroup);
	}

	return false;
}

function getOidcDescription(integration?: IIntegration): ReactNode {
	if (integration?.type === 'microstrategy') {
		return (
			<Text size="sm" color="text/secondary/default">
				Please enter your MicroStrategy Identity Token to authenticate this
				sync. This can be found from your OIDC Identity Provider.
			</Text>
		);
	}
	return (
		<Text size="sm" color="text/secondary/default">
			Please enter your identity token from your identity provider.
		</Text>
	);
}

async function promptForIdentityToken(
	title: string,
	description: ReactNode
): Promise<string | undefined> {
	return new Promise<string | undefined>((resolve) => {
		openModal({
			title,
			children: (
				<IdentityTokenModal
					onConfirm={(token) => {
						closeAllModals();
						resolve(token);
					}}
					onCancel={() => {
						closeAllModals();
						resolve(undefined);
					}}
					description={description}
				/>
			),
		});
	});
}

/**
 * If OIDC is enabled, prompt for the identity token.
 * @param options - The options for the identity token.
 * Only one of ( integration ) or ( integrationSpec & selectedGroup ) should be provided.
 * @returns The identity token if it is provided, otherwise undefined.
 */
export async function getIdentityTokenIfOidc(options: {
	integrationSpec?: IntegrationSpec;
	integration?: IIntegration;
	selectedGroup?: string;
}): Promise<string | undefined> {
	const { integration, integrationSpec, selectedGroup } = options || {};

	/* Since JS doesn't support overloaded functions, the simplest way to maintain this
	 * exported function's signature without creating multiple duplicate functions is to
	 * document that only one of ( integration ) or ( integrationSpec & selectedGroup )
	 * should be provided. */
	if (!shouldUseOidc(integrationSpec, selectedGroup, integration)) {
		return undefined;
	}

	const identityToken = await promptForIdentityToken(
		'Enter OIDC identity token',
		getOidcDescription(integration)
	);

	return identityToken;
}

/**
 * Determines if the sync should be cancelled due to the absence of an identity token.
 * Uses the same logic as getIdentityTokenIfOidc to determine if OIDC is enabled.
 * @param identityToken - The identity token to check.
 * @param options - The options for the sync.
 * @returns { shouldCancel: boolean; error?: Error } - The result of the check.
 */
export function shouldCancelSyncForOidc(
	identityToken: string | undefined,
	options?: {
		integrationSpec?: IntegrationSpec;
		selectedGroup?: string;
		integration?: IIntegration;
		integrationName?: string;
	}
): { shouldCancel: boolean; error?: Error } {
	const { integrationSpec, selectedGroup, integration, integrationName } =
		options || {};

	/* Since JS doesn't support overloaded functions, the simplest way to maintain this
	 * exported function's signature without creating multiple duplicate functions is to
	 * document that only one of ( integration ) or ( integrationSpec & selectedGroup )
	 * should be provided. */
	const isUsingOidc = shouldUseOidc(
		integrationSpec,
		selectedGroup,
		integration
	);

	if (identityToken === undefined && isUsingOidc) {
		return {
			shouldCancel: true,
			error: integrationName
				? new Error(
						`Cancelled sync for ${integrationName}: identity token required`
					)
				: new Error('Cancelled sync: identity token required'),
		};
	}

	return { shouldCancel: false };
}
